import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, StatCard, Card, Badge } from "@/components/admin/ui";
import { formatDate } from "@/lib/format";

export default function Dashboard() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await api.get("/admin/stats")).data, staleTime: 0 });

  if (isLoading || !data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div>;

  return (
    <div data-testid="admin-dashboard">
      <PageTitle eyebrow="Overview" title="Dashboard">
        <Link to="/admin/vehicles/new" className="btn-crimson !py-2.5" data-testid="dashboard-add-vehicle-button">Add vehicle</Link>
      </PageTitle>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="New leads" value={data.leads_new} sub={`${data.leads_week} in the last 7 days`} testid="stat-leads-new" />
        <StatCard label="Test drive requests" value={data.test_drives} sub="All time" testid="stat-test-drives" />
        <StatCard label="Enquiries" value={data.enquiries} sub="All time" testid="stat-enquiries" />
        <StatCard label="Vehicles" value={data.vehicles} sub={`${data.published} published · ${data.gallery} gallery images`} testid="stat-vehicles" />
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-3">
        <Card title="Recent leads" className="xl:col-span-2" action={<Link to="/admin/leads" className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.16em] text-crimson" data-testid="dashboard-all-leads-link">All leads <ArrowUpRight className="h-3 w-3" /></Link>}>
          {data.recent_leads.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="dashboard-no-leads">No leads yet. They will appear here as soon as someone submits a form on the website.</p>
          ) : (
            <ul className="divide-y divide-white/10" data-testid="dashboard-recent-leads">
              {data.recent_leads.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div>
                    <p className="font-display font-semibold">{l.name} <span className="font-mono text-xs text-slate-500">· {l.phone}</span></p>
                    <p className="text-xs text-slate-500">{l.vehicle || "—"} · {formatDate(l.created_at)}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge tone={l.type === "test_drive" ? "gold" : "slate"}>{l.type === "test_drive" ? "Test drive" : "Enquiry"}</Badge>
                    <Badge tone={l.status === "new" ? "red" : l.status === "contacted" ? "gold" : "green"}>{l.status}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Quick actions">
          <div className="space-y-2">
            {[["/admin/vehicles", "Update prices & models"], ["/admin/gallery", "Add gallery photos"], ["/admin/settings", "Edit contact details & hours"], ["/admin/leads", "Follow up on leads"]].map(([to, label]) => (
              <Link key={to} to={to} className="flex items-center justify-between rounded-md border border-white/10 px-4 py-3 text-sm transition-colors hover:border-white/30 hover:bg-white/5">{label} <ArrowUpRight className="h-4 w-4 text-slate-500" /></Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
